import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { PlayerAuthApiService } from '../core/player-auth-api.service';
import { PlayerPrivateProfile } from '../core/players-api.types';
import { SessionService } from '../core/session.service';

@Component({
  selector: 'app-player-profile-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="page">
      <header class="card header">
        <h1>{{ t('player.profile.title') }}</h1>
        <p>{{ t('player.profile.subtitle') }}</p>
      </header>

      @if (loading()) {
        <p class="state">{{ t('player.profile.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else {
        <article class="card">
          <form (ngSubmit)="save()" #form="ngForm" class="form">
            <label>{{ t('player.profile.emailReadonly') }}
              <input type="email" [ngModel]="email" name="email" readonly />
            </label>
            <label>{{ t('player.profile.pseudo') }}
              <input type="text" required [(ngModel)]="pseudo" name="pseudo" />
            </label>
            <label>{{ t('player.profile.activision') }}
              <input type="text" required [(ngModel)]="activisionId" name="activisionId" />
            </label>
            <label>{{ t('player.profile.avatar') }}
              <input type="url" [(ngModel)]="avatarUrl" name="avatarUrl" />
            </label>
            <label>{{ t('player.profile.country') }}
              <input type="text" maxlength="2" [(ngModel)]="countryCode" name="countryCode" />
            </label>

            <button type="submit" [disabled]="saving() || !form.valid">
              {{ saving() ? t('player.profile.saving') : t('player.profile.save') }}
            </button>
          </form>
        </article>

        @if (successMessage()) {
          <p class="state state--success">{{ successMessage() }}</p>
        }
      }
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 760px; margin: 0 auto; }
    .card { border: 1px solid var(--line-soft); border-radius: 12px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 1rem; box-shadow: var(--shadow-soft); }
    .header { margin-bottom: 0.8rem; }
    h1 { margin: 0 0 0.3rem; font-size: 1.45rem; color: var(--text-strong); }
    .header p { margin: 0; color: var(--text-muted); }
    .form { display: grid; gap: 0.78rem; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; color: #dbe9ff; }
    input { padding: 0.55rem 0.6rem; border: 1px solid var(--line-soft); border-radius: 8px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); }
    input[readonly] { background: rgba(84, 99, 124, 0.28); color: #d4e1f8; }
    button { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.6rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; grid-column: 1 / -1; justify-self: start; }
    button:disabled { opacity: 0.7; }
    .state { margin: 0.6rem 0 0; color: var(--text-muted); }
    .state--error { color: var(--danger); }
    .state--success { color: var(--ok-text); }
  `
})
export class PlayerProfilePageComponent {
  private readonly api = inject(PlayerAuthApiService);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly errorMessage = signal<string | null>(null);

  protected email = '';
  protected pseudo = '';
  protected activisionId = '';
  protected avatarUrl = '';
  protected countryCode = '';

  constructor() {
    this.loadProfile();
  }

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected save(): void {
    this.saving.set(true);
    this.successMessage.set(null);
    this.errorMessage.set(null);

    this.api
      .updateMe({
        pseudo: this.pseudo.trim(),
        activision_id: this.activisionId.trim(),
        avatar_url: this.avatarUrl.trim(),
        country_code: this.countryCode.trim().toUpperCase()
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.applyProfile(response.profile);
          this.successMessage.set(this.t('player.profile.updated'));
          this.saving.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(Array.isArray(apiErrors) ? apiErrors.join(' | ') : this.t('player.profile.updateError'));
          this.saving.set(false);
        }
      });
  }

  private loadProfile(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.api
      .getMe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.applyProfile(response.profile);
          this.loading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          this.errorMessage.set(this.t('player.profile.loadError'));
          this.loading.set(false);
        }
      });
  }

  private applyProfile(profile: PlayerPrivateProfile): void {
    this.email = profile.email;
    this.pseudo = profile.pseudo;
    this.activisionId = profile.activision_id;
    this.avatarUrl = profile.avatar_url ?? '';
    this.countryCode = profile.country_code ?? '';
  }

  private handleUnauthorized(): void {
    this.session.clearToken();
    void this.router.navigateByUrl('/player/login');
  }
}
